import { API_URL, WS_URL } from './api.js';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth }     from './contexts/AuthContext';
import { useLang }     from './contexts/LangContext';
import { useNavigate, useParams } from 'react-router-dom';
import useBackButton from './hooks/useBackButton';
import {
  Search, MessageCircle, Bell, Menu, X, ChevronDown, Plus, Compass,
  GraduationCap, ArrowLeft, Send, Heart, MessageSquare, UserPlus,
  FileText, Calendar, DollarSign, Megaphone, LogOut, Settings, User, Users, Shield
} from 'lucide-react';

import Toast          from './components/Toast';
import ThemeSwitcher  from './components/ThemeSwitcher';
import LangSwitcher   from './components/LangSwitcher';
import ChatDrawer     from './components/ChatDrawer';
import Avatar         from './components/Avatar';

import DashboardTab    from './tabs/DashboardTab';
import CommunityTab    from './tabs/CommunityTab';
import ClassroomTab    from './tabs/ClassroomTab';
import CalendarTab     from './tabs/CalendarTab';
import LeaderboardTab  from './tabs/LeaderboardTab';
import MembersTab      from './tabs/MembersTab';
import AboutTab        from './tabs/AboutTab';
import PlansTab        from './tabs/PlansTab';
import ProfileTab      from './tabs/ProfileTab';
import AdminPlansTab   from './tabs/AdminPlansTab';
import LangEditorTab   from './tabs/LangEditorTab';
import AdminDashboardTab from './tabs/AdminDashboardTab';
import AdminUsersTab     from './tabs/AdminUsersTab';
import AdminCommunitiesTab from './tabs/AdminCommunitiesTab';
import AdminCoursesTab     from './tabs/AdminCoursesTab';
import AdminSettingsTab    from './tabs/AdminSettingsTab';

import './App.css';

const USER_NAV = [
  { key: 'community',    label: 'Community' },
  { key: 'classroom',    label: 'Classroom' },
  { key: 'calendar',     label: 'Calendar' },
  { key: 'members',      label: 'Members' },
  { key: 'leaderboards', label: 'Leaderboards' },
  { key: 'about',        label: 'About' },
];

const ADMIN_NAV = [
  { key: 'admin_dashboard',   label: 'Dashboard' },
  { key: 'admin_users',       label: 'Users' },
  { key: 'admin_communities', label: 'Communities' },
  { key: 'admin_courses',     label: 'Courses' },
  { key: 'adminplans',        label: 'Plans' },
  { key: 'langeditor',        label: 'Language' },
  { key: 'admin_settings',    label: 'Settings' },
];

// Lucide icon components for notification types
const NOTIF_ICON = {
  like:    Heart,
  comment: MessageSquare,
  follow:  UserPlus,
  post:    FileText,
  event:   Calendar,
  sale:    DollarSign,
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return min + 'm';
  const h = Math.floor(min / 60);
  if (h < 24) return h + 'h';
  return Math.floor(h / 24) + 'd';
}

function timeStr(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dateLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString())     return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Shell() {
  const { user, logout, token } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const { slug, tab, courseId, lessonId } = useParams();
  const isAdmin  = user?.role === 'admin';

  // The active tab is driven by the URL (/:slug/:tab); default to the feed.
  const page = tab || (isAdmin ? 'admin_dashboard' : 'community');
  const [toast, setToast] = useState(null);

  const [communities, setCommunities] = useState([]);
  const [activeCommunity, setActiveCommunity] = useState(null);

  const fetchCommunities = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { 'Authorization': 'Bearer ' + token };
      const res = await fetch(API_URL + '/api/communities?my=true', { headers });
      if (res.ok) {
        const data = await res.json();
        setCommunities(data || []);
        if (isAdmin) {
          // Administrators stay in the global System Admin Console
          return;
        }
        // Resolve the active community from the URL slug (match slug OR numeric id).
        const found = slug && data ? data.find(c => c.slug === slug || String(c.id) === slug) : null;
        if (found) {
          setActiveCommunity(found);
          localStorage.setItem('activeCommunitySlug', found.slug || String(found.id));
          // If loaded via numeric id but slug exists, redirect to slug-based URL for clean routing
          if (slug === String(found.id) && found.slug) {
            navigate('/' + found.slug + '/' + (tab || 'community'), { replace: true });
          }
        } else if (slug) {
          // Slug not in my-list — fetch it directly (e.g. not yet joined / legacy).
          try {
            const r2 = await fetch(API_URL + '/api/communities/' + slug, { headers });
            if (r2.ok) {
              const comm = await r2.json();
              setActiveCommunity(comm);
              if (comm) {
                localStorage.setItem('activeCommunitySlug', comm.slug || String(comm.id));
                if (slug === String(comm.id) && comm.slug) {
                  navigate('/' + comm.slug + '/' + (tab || 'community'), { replace: true });
                }
              }
            }
          } catch {}
        } else if (data && data.length > 0) {
          // No slug in the URL (e.g. /app) → redirect to the saved or first community.
          const savedSlug = localStorage.getItem('activeCommunitySlug');
          const target = data.find(c => (c.slug || String(c.id)) === savedSlug) || data[0];
          navigate('/' + (target.slug || target.id) + '/community', { replace: true });
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [token, slug, navigate, isAdmin, tab]);

  useEffect(() => { fetchCommunities(); }, [fetchCommunities]);


  function selectCommunity(community) {
    setActiveCommunity(community);
    const key = community.slug || community.id;
    localStorage.setItem('activeCommunitySlug', String(key));
    closeAll();
    navigate('/' + key + '/community');
  }

  const [showCommunityDrop, setShowCommunityDrop] = useState(false);
  const [showUserDrop,      setShowUserDrop]      = useState(false);
  const [showNotifPanel,    setShowNotifPanel]    = useState(false);
  const [showChatPanel,     setShowChatPanel]     = useState(false);
  const [previewImage,    setPreviewImage]    = useState(null);
  const [uploadingChat,   setUploadingChat]   = useState(false);
  const chatFileInputRef = useRef(null);
  const [showDrawer,        setShowDrawer]        = useState(false);
  const [searchQuery,       setSearchQuery]       = useState('');

  useBackButton(() => {
    if (previewImage) { setPreviewImage(null); }
    else if (showChatPanel) { setShowChatPanel(false); }
    else if (showNotifPanel) { setShowNotifPanel(false); }
    else if (showUserDrop) { setShowUserDrop(false); }
    else if (showCommunityDrop) { setShowCommunityDrop(false); }
    else if (showDrawer) { setShowDrawer(false); }
  }, !!(previewImage || showChatPanel || showNotifPanel || showUserDrop || showCommunityDrop || showDrawer));

  const [searchPosts, setSearchPosts] = useState([]);
  const [searchCourses, setSearchCourses] = useState([]);
  const [searchMembers, setSearchMembers] = useState([]);
  const [searchEvents, setSearchEvents] = useState([]);
  const [searchLoadedCommId, setSearchLoadedCommId] = useState(null);
  const [activeSearchTab, setActiveSearchTab] = useState('community');

  useEffect(() => {
    if (!activeCommunity?.id || !token) return;
    if (searchLoadedCommId !== activeCommunity.id) {
      setSearchPosts([]);
      setSearchCourses([]);
      setSearchMembers([]);
      setSearchEvents([]);
      setSearchLoadedCommId(null);
    }
  }, [activeCommunity?.id, token, searchLoadedCommId]);

  useEffect(() => {
    if (!activeCommunity?.id || !token || !searchQuery.trim()) return;
    if (searchLoadedCommId === activeCommunity.id) return;

    const headers = { 'Authorization': 'Bearer ' + token };
    const commId = activeCommunity.id;
    setSearchLoadedCommId(commId);

    fetch(`${API_URL}/api/community/posts?communityId=${commId}`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (Array.isArray(data)) setSearchPosts(data); })
      .catch(() => {});

    fetch(`${API_URL}/api/courses?communityId=${commId}`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (Array.isArray(data)) setSearchCourses(data); })
      .catch(() => {});

    fetch(`${API_URL}/api/communities/${commId}/members`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (Array.isArray(data)) setSearchMembers(data); })
      .catch(() => {});

    fetch(`${API_URL}/api/community/events`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (Array.isArray(data)) setSearchEvents(data); })
      .catch(() => {});
  }, [activeCommunity?.id, token, searchQuery, searchLoadedCommId]);

  const q = searchQuery.toLowerCase().trim();

  const matchingPosts = q ? searchPosts.filter(p => {
    return (p.content || '').toLowerCase().includes(q) || 
           (p.authorName || '').toLowerCase().includes(q) ||
           (p.tags || []).some(t => t.toLowerCase().includes(q));
  }) : [];

  const matchingCourses = q ? searchCourses.filter(c => {
    return (c.title || '').toLowerCase().includes(q) || 
           (c.description || '').toLowerCase().includes(q);
  }) : [];

  const matchingMembers = q ? searchMembers.filter(m => {
    const name = (m.displayName || m.name || '').toLowerCase();
    const handle = (m.handle || '').toLowerCase();
    const bio = (m.bio || '').toLowerCase();
    return name.includes(q) || handle.includes(q) || bio.includes(q);
  }) : [];

  const matchingEvents = q ? searchEvents.filter(e => {
    return (e.title || '').toLowerCase().includes(q) || 
           (e.description || '').toLowerCase().includes(q);
  }) : [];

  const isSearchActive = searchQuery.trim() !== '';

  const prevSearchRef = useRef('');
  useEffect(() => {
    const prev = prevSearchRef.current.trim();
    const curr = searchQuery.trim();
    if (curr && !prev) {
      if (['community', 'classroom', 'calendar', 'members'].includes(page)) {
        setActiveSearchTab(page);
      } else {
        setActiveSearchTab('community');
      }
    }
    prevSearchRef.current = searchQuery;
  }, [searchQuery, page]);

  useEffect(() => {
    setSearchQuery('');
  }, [slug]);

  const [notifs,      setNotifs]      = useState([]);
  const [notifFilter, setNotifFilter] = useState('all');
  const unreadCount = notifs.filter(n => !n.is_read).length;

  const [chatTarget,     setChatTarget]     = useState(null);
  const [conversations,  setConversations]  = useState([]);
  const [activeMembership, setActiveMembership] = useState(null);

  const fetchActiveMembership = useCallback(async () => {
    if (!activeCommunity || !token) {
      setActiveMembership(null);
      return;
    }
    try {
      const headers = { 'Authorization': 'Bearer ' + token };
      const res = await fetch(API_URL + '/api/communities/' + activeCommunity.id + '/membership', { headers });
      if (res.ok) {
        const data = await res.json();
        setActiveMembership(data);
      }
    } catch (err) {
      console.error("Failed to fetch active membership status", err);
    }
  }, [activeCommunity, token]);

  useEffect(() => {
    fetchActiveMembership();
  }, [fetchActiveMembership]);
  const [activeChatConv, setActiveChatConv] = useState(null);
  const activeChatConvRef = useRef(activeChatConv);
  const showChatPanelRef = useRef(showChatPanel);
  useEffect(() => { activeChatConvRef.current = activeChatConv; }, [activeChatConv]);
  useEffect(() => { showChatPanelRef.current = showChatPanel; }, [showChatPanel]);
  
  const [chatMessages,   setChatMessages]   = useState([]);
  const [chatInput,      setChatInput]      = useState('');
  const [chatSearch,     setChatSearch]     = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.shell-notif-area'))         setShowNotifPanel(false);
      if (!e.target.closest('.shell-chat-area'))          setShowChatPanel(false);
      if (!e.target.closest('.qeduvo-logo-area'))         setShowCommunityDrop(false);
      if (!e.target.closest('.qeduvo-user-dropdown-area')) setShowUserDrop(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = showDrawer ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showDrawer]);

  const fetchNotifs = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(API_URL + '/api/notifications', { headers: { Authorization: 'Bearer ' + token } });
      if (res.ok) setNotifs(await res.json());
    } catch {}
  }, [token]);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(API_URL + '/api/chat/conversations', { headers: { Authorization: 'Bearer ' + token } });
      if (res.ok) {
        const data = await res.json();
        const mapped = data.map(c => ({
          ...c,
          other_name: c.other_name || c.otherName,
          other_avatar: c.other_avatar || c.otherAvatar,
          last_message: c.last_message || c.lastMessage,
          last_at: c.last_at || c.lastMessageAt,
          unread_count: c.unread_count || c.unreadCount || 0,
        }));
        setConversations(mapped);
      }
    } catch {}
  }, [token]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);
  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // ── Chat WebSocket connection for real-time messages in the popup ──────────
  const chatWsRef = useRef(null);
  const chatReconnTimer = useRef(null);

  const connectChatWS = useCallback(() => {
    if (!activeChatConv?.id || !token) return;

    const ws = new WebSocket(WS_URL);
    chatWsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'chat', convId: activeChatConv.id, token }));
    };

    ws.onmessage = (ev) => {
      try {
        const frame = JSON.parse(ev.data);
        if (frame.type === 'chat_message' && frame.payload) {
          const msg = typeof frame.payload === 'string'
            ? JSON.parse(frame.payload)
            : frame.payload;
          if (msg.senderId !== user?.id) {
            setChatMessages(prev => {
              const dup = prev.some(m => m.id === msg.id);
              if (dup) return prev;
              return [...prev, msg];
            });
            // Update last message preview in conversations list
            setConversations(prev => prev.map(c => c.id === activeChatConv.id ? { ...c, last_message: msg.content || msg.attachmentName } : c));
          }
        }
      } catch (_) {}
    };

    ws.onclose = () => {
      chatReconnTimer.current = setTimeout(connectChatWS, 3000);
    };

    ws.onerror = () => ws.close();
  }, [activeChatConv?.id, token, user?.id]);

  useEffect(() => {
    connectChatWS();
    return () => {
      clearTimeout(chatReconnTimer.current);
      chatWsRef.current?.close();
    };
  }, [connectChatWS]);

  // ── Real-time notifications via personal WebSocket channel ──────────────────
  const notifWsRef = useRef(null);
  useEffect(() => {
    if (!token || !user) return;
    let ws;
    let reconnectTimer;
    let dead = false;

    function connect() {
      ws = new WebSocket(WS_URL);
      notifWsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'notif', token }));
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'notification' && msg.payload) {
            const parsed = typeof msg.payload === 'string' ? JSON.parse(msg.payload) : msg.payload;
            if (parsed.conversationId) {
              const chatMsg = parsed;
              if (chatMsg.senderId !== user?.id) {
                if (activeChatConvRef.current?.id === chatMsg.conversationId) {
                  setChatMessages(prev => {
                    const dup = prev.some(m => m.id === chatMsg.id);
                    if (dup) return prev;
                    return [...prev, chatMsg];
                  });
                }
                setConversations(prev => {
                  const exists = prev.some(c => c.id === chatMsg.conversationId);
                  if (!exists) {
                    fetchConversations();
                    return prev;
                  }
                  return prev.map(c => {
                    if (c.id === chatMsg.conversationId) {
                      const isCurrentActive = activeChatConvRef.current?.id === chatMsg.conversationId && showChatPanelRef.current;
                      return {
                        ...c,
                        last_message: chatMsg.content || chatMsg.attachmentName,
                        last_at: chatMsg.createdAt,
                        unread_count: isCurrentActive ? 0 : (c.unread_count || 0) + 1
                      };
                    }
                    return c;
                  });
                });
              }
            } else {
              setNotifs(prev => [parsed, ...prev]);
            }
          }
        } catch {}
      };

      ws.onclose = () => {
        if (!dead) reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => ws.close();
    }

    connect();
    return () => {
      dead = true;
      clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [token, user]);

  async function markAllRead() {
    try {
      await fetch(API_URL + '/api/notifications/read-all', { method: 'POST', headers: { Authorization: 'Bearer ' + token } });
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  }

  async function markOneRead(notif) {
    if (notif.is_read) return;
    try {
      await fetch(`${API_URL}/api/notifications/${notif.id}/read`, { method: 'POST', headers: { Authorization: 'Bearer ' + token } });
      setNotifs(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    } catch {}
  }

  async function handleNotifClick(notif) {
    await markOneRead(notif);
    setShowNotifPanel(false);

    if (notif.entityType === 'post' || notif.type === 'like' || notif.type === 'comment') {
      const commKey = notif.communityId || slug || (activeCommunity?.slug || activeCommunity?.id);
      if (commKey && notif.entityId) {
        navigate(`/${commKey}/community?post=${notif.entityId}`);
      }
    } else if (notif.type === 'follow') {
      const parts = notif.actor_name?.split(' ');
      const username = parts?.[0]?.toLowerCase() || 'me';
      navigate(`/@${username}`);
    } else if (notif.entityType === 'event') {
      const commKey = notif.communityId || slug || (activeCommunity?.slug || activeCommunity?.id);
      if (commKey) {
        navigate(`/${commKey}/calendar`);
      }
    }
  }

  async function openChat(conv) {
    setActiveChatConv(conv);
    setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
    try {
      const res = await fetch(API_URL + '/api/chat/conversations/' + conv.id + '/messages', { headers: { Authorization: 'Bearer ' + token } });
      if (res.ok) setChatMessages(await res.json());
    } catch {}
  }

  async function sendChatMessage() {
    if (!chatInput.trim() || !activeChatConv) return;
    const content = chatInput.trim();
    setChatInput('');
    try {
      const res = await fetch(API_URL + '/api/chat/conversations/' + activeChatConv.id + '/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ content })
      });
      if (res.ok) {
        const msg = await res.json();
        setChatMessages(prev => [...prev, { ...msg, sender_name: user?.display_name }]);
        setConversations(prev => prev.map(c => c.id === activeChatConv.id ? { ...c, last_message: content } : c));
      }
    } catch {}
  }

  async function handleChatFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !activeChatConv) return;
    e.target.value = '';

    setUploadingChat(true);
    const ext = '.' + (file.name.split('.').pop() || 'bin');
    
    const checkExt = ext.toLowerCase();
    let attachmentType = 'file';
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(checkExt)) attachmentType = 'image';
    else if (['.mp4', '.webm', '.mov', '.avi', '.mkv'].includes(checkExt)) attachmentType = 'video';
    
    const attachmentName = file.name;

    try {
      const uploadRes = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const { url } = await uploadRes.json();

      const res = await fetch(`${API_URL}/api/chat/conversations/${activeChatConv.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ attachmentUrl: url, attachmentType, attachmentName }),
      });
      if (res.ok) {
        const saved = await res.json();
        setChatMessages(prev => [...prev, { ...saved, sender_name: user?.display_name }]);
        setConversations(prev => prev.map(c => c.id === activeChatConv.id ? { ...c, last_message: attachmentName } : c));
      }
    } catch (_) {}
    setUploadingChat(false);
  }

  function renderAttachmentPreview(url, type, name, isMe) {
    const fullUrl = `${API_URL}${url}`;

    if (type === 'image') {
      return (
        <div 
          onClick={() => setPreviewImage(fullUrl)} 
          style={{ display: 'block', marginTop: 4, cursor: 'pointer' }}
        >
          <img
            src={fullUrl}
            alt={name || 'image'}
            style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, display: 'block', objectFit: 'cover' }}
          />
        </div>
      );
    }

    if (type === 'video') {
      return (
        <video
          src={fullUrl}
          controls
          style={{ maxWidth: '100%', borderRadius: 8, marginTop: 4, display: 'block' }}
        />
      );
    }

    return (
      <a
        href={fullUrl}
        target="_blank"
        rel="noreferrer"
        download={name}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 4,
          padding: '6px 10px',
          background: isMe ? 'rgba(255,255,255,0.15)' : 'var(--surface)',
          border: `1px solid ${isMe ? 'rgba(255,255,255,0.2)' : 'var(--border)'}`,
          borderRadius: 8,
          textDecoration: 'none',
          color: isMe ? '#fff' : 'var(--text)',
          fontSize: 12,
          maxWidth: '100%',
          wordBreak: 'break-all',
        }}
      >
        <span style={{ fontSize: 16 }}>📎</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name || 'File'}
        </span>
        <span style={{ fontSize: 10, opacity: 0.7, flexShrink: 0 }}>↓</span>
      </a>
    );
  }

  function closeAll() { setShowCommunityDrop(false); setShowUserDrop(false); setShowNotifPanel(false); setShowChatPanel(false); }
  function handleLogout() { logout(); navigate('/', { replace: true }); }

  // ── DM level gate ──
  function getRequiredLevelForPerk(levelConfigsJson, keyword, defaultLevel) {
    if (!levelConfigsJson) return defaultLevel;
    try {
      const decoded = JSON.parse(levelConfigsJson);
      if (Array.isArray(decoded)) {
        for (const lvlObj of decoded) {
          if (Array.isArray(lvlObj.perks)) {
            for (const perk of lvlObj.perks) {
              if (perk.toLowerCase().includes(keyword.toLowerCase())) return lvlObj.level || defaultLevel;
            }
          }
        }
      }
    } catch {}
    return defaultLevel;
  }
  const isOwnerOrAdmin  = user?.role === 'admin' || Number(user?.id) === Number(activeCommunity?.ownerId) || activeMembership?.role === 'admin';
  const dmRequiredLevel = getRequiredLevelForPerk(activeCommunity?.levelConfigs, 'messaging', 2);
  const userLevel       = activeMembership?.level ?? 1;
  const hasDmUnlocked   = isOwnerOrAdmin || userLevel >= dmRequiredLevel;

  // Gated wrapper: shows a toast if L1 user tries to open DM
  function openChatGated(targetUser) {
    if (!hasDmUnlocked) {
      setToast(`🔒 Direct Messaging unlocks at Level ${dmRequiredLevel}. You are currently Level ${userLevel}.`);
      return;
    }
    setChatTarget(targetUser);
  }

  const ALL_NAV = isAdmin ? ADMIN_NAV : USER_NAV;
  const PAGES = {
    dashboard:    <DashboardTab />,
    community:    <CommunityTab    onToast={setToast} community={activeCommunity} activeMembership={activeMembership} searchQuery={searchQuery} />,
    classroom:    <ClassroomTab    onToast={setToast} community={activeCommunity} initialCourseId={courseId} initialLessonId={lessonId} activeMembership={activeMembership} searchQuery={searchQuery} />,
    calendar:     <CalendarTab     onToast={setToast} community={activeCommunity} activeMembership={activeMembership} searchQuery={searchQuery} />,
    leaderboards: <LeaderboardTab community={activeCommunity} activeMembership={activeMembership} onSaved={fetchActiveMembership} />,
    members:      <MembersTab      community={activeCommunity} onChatOpen={openChatGated} activeMembership={activeMembership} searchQuery={searchQuery} />,
    about:        <AboutTab        community={activeCommunity} onToast={setToast} />,
    plans:        <PlansTab        onToast={setToast} />,
    profile:      <ProfileTab      onToast={setToast} />,
    
    // Administrator pages
    admin_dashboard:   <AdminDashboardTab onToast={setToast} />,
    admin_users:       <AdminUsersTab     onToast={setToast} />,
    admin_communities: <AdminCommunitiesTab onToast={setToast} />,
    admin_courses:     <AdminCoursesTab     onToast={setToast} />,
    adminplans:        <AdminPlansTab   onToast={setToast} />,
    langeditor:        <LangEditorTab   onToast={setToast} />,
    admin_settings:    <AdminSettingsTab    onToast={setToast} />,
  };
  const initial   = (user?.display_name?.[0] || '?').toUpperCase();
  const avatarUrl = user?.avatar_url ? API_URL + user.avatar_url : null;
  const filteredNotifs = notifFilter === 'all' ? notifs : notifs.filter(n => n.type === notifFilter);
  const filteredConvs  = conversations.filter(c => !chatSearch || c.other_name?.toLowerCase().includes(chatSearch.toLowerCase()));
  const totalUnreadChats = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  function goToPage(key) { setShowDrawer(false); navigate('/' + (slug || 'app') + '/' + key); }

  return (
    <>
      <div className="app">
        <header className="qeduvo-header">
          <div className="qeduvo-header-inner">

            {/* ── Community Switcher / Admin Header ── */}
            {isAdmin ? (
              <div className="qeduvo-logo-area" style={{ position: 'relative', minWidth: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="qeduvo-logo-img" style={{ background: 'var(--accent-grad)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                    <Shield size={18} />
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: '0.5px', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Qeduvo <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 11, background: 'var(--accent-soft)', padding: '2px 8px', borderRadius: 12 }}>Admin</span>
                  </span>
                </div>
              </div>
            ) : (
              <div className="qeduvo-logo-area" style={{ position: 'relative', cursor: 'pointer' }}
                onClick={() => { const v = !showCommunityDrop; closeAll(); setShowCommunityDrop(v); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="qeduvo-logo-img" style={{
                    background: activeCommunity?.coverImageUrl
                      ? 'transparent'
                      : 'var(--accent-grad)',
                    color: '#fff',
                    fontSize: 20
                  }}>
                    {activeCommunity?.coverImageUrl ? (
                      <img
                        src={`${API_URL}${activeCommunity.coverImageUrl}`}
                        alt=""
                        style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }}
                      />
                    ) : (
                      activeCommunity?.emoji || <GraduationCap size={18} />
                    )}
                  </div>
                  <span style={{ fontWeight: 700, fontSize: 15, display:'flex', alignItems:'center', gap:5 }}>
                    {activeCommunity ? activeCommunity.name : 'Select Community'}
                    <ChevronDown size={14} style={{ opacity: 0.5 }} />
                  </span>
                </div>

              {showCommunityDrop && (
                <div className="card shell-panel" onClick={e => e.stopPropagation()}
                  style={{ top:52, left:0, right:'auto', width:280, padding:12, marginBottom:0 }}>
                  <div style={{ background:'var(--surface2)', borderRadius:8, display:'flex', alignItems:'center', padding:'8px 12px', marginBottom:12 }}>
                    <Search size={15} style={{ color:'var(--text3)', marginRight:8 }} />
                    <input type="text" placeholder="Search communities" style={{ border:'none', background:'transparent', outline:'none', width:'100%', fontSize:13, color:'var(--text)' }} />
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                    <div className="dropdown-item-hover" style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px', borderRadius:8, cursor:'pointer' }} onClick={() => { navigate('/create'); closeAll(); }}>
                      <div style={{ width:26, height:26, borderRadius:7, background:'var(--surface2)', display:'flex', alignItems:'center', justifyContent:'center' }}><Plus size={15} /></div>
                      <span style={{ fontSize:13, fontWeight:600 }}>Create a community</span>
                    </div>
                    <div className="dropdown-item-hover" style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px', borderRadius:8, cursor:'pointer' }}
                      onClick={() => { navigate('/'); closeAll(); }}>
                      <div style={{ width:26, height:26, borderRadius:7, background:'var(--surface2)', display:'flex', alignItems:'center', justifyContent:'center' }}><Compass size={15} /></div>
                      <span style={{ fontSize:13, fontWeight:600 }}>Discover communities</span>
                    </div>
                  </div>
                  {communities.length > 0 && (
                    <>
                      <div className="divider" style={{ margin:'12px 0' }} />
                      <div style={{ display:'flex', flexDirection:'column', gap:2, maxHeight:180, overflowY:'auto' }}>
                        {communities.map(c => (
                          <div key={c.id} className="dropdown-item-hover"
                            style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px', borderRadius:8, cursor:'pointer',
                              background: activeCommunity?.id === c.id ? 'var(--accent-soft)' : 'transparent',
                              fontWeight: activeCommunity?.id === c.id ? 700 : 500 }}
                            onClick={() => selectCommunity(c)}>
                            <div style={{
                              width:26, height:26, borderRadius:7,
                              background: c.coverImageUrl
                                ? `url(${API_URL}${c.coverImageUrl}) center/cover no-repeat`
                                : 'var(--accent-grad)',
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:12, color:'#fff'
                            }}>
                              {!c.coverImageUrl && (c.emoji || <GraduationCap size={14} />)}
                            </div>
                            <span style={{ fontSize:13, color:'var(--text)' }}>{c.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Admin Navigation links in top header nav (middle/left align) */}
          {isAdmin && (
            <nav className="hide-on-mobile" style={{ display: 'flex', gap: 6, flex: 1, marginLeft: 20 }}>
              {ADMIN_NAV.map(n => (
                <div 
                  key={n.key} 
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: page === n.key ? 'var(--accent)' : 'var(--text2)',
                    background: page === n.key ? 'var(--accent-soft)' : 'transparent',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={() => navigate('/' + (slug || 'app') + '/' + n.key)}
                >
                  {t('nav.' + n.key, n.label)}
                </div>
              ))}
            </nav>
          )}

          {/* ── Mobile hamburger ── */}
          <button className="mobile-nav-toggle" aria-label="Open menu" onClick={() => setShowDrawer(true)} style={{ marginLeft: isAdmin ? 'auto' : 0 }}>
            <Menu size={22} />
          </button>

          {/* ── Search ── */}
          {!isAdmin && (
            <div className="qeduvo-search-area">
              <div className="qeduvo-search-box">
                <Search size={17} style={{ color:'var(--text3)' }} />
                <input 
                  type="text" 
                  placeholder="Search..." 
                  aria-label="Search" 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ── Right Actions ── */}
          <div className="qeduvo-actions-area">
            <span className="hide-on-mobile" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <ThemeSwitcher />
              <LangSwitcher />
            </span>

            {/* Chat */}
            <div className="shell-chat-area" style={{ position:'relative' }}>
              <button className="qeduvo-icon-btn" aria-label="Messages"
                onClick={() => {
                  if (!hasDmUnlocked) {
                    setToast(`🔒 Direct Messaging unlocks at Level ${dmRequiredLevel}. Earn more XP to unlock!`);
                    return;
                  }
                  const v = !showChatPanel; closeAll(); setShowChatPanel(v); if (v) fetchConversations();
                }}
                title={hasDmUnlocked ? 'Messages' : `Unlock messaging at Level ${dmRequiredLevel}`}
                style={!hasDmUnlocked ? { opacity: 0.5, cursor: 'not-allowed', position: 'relative' } : { position: 'relative' }}
              >
                <MessageCircle size={20} />
                {hasDmUnlocked && totalUnreadChats > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    minWidth: 16,
                    height: 16,
                    background: 'var(--accent)',
                    borderRadius: 8,
                    border: '2px solid var(--surface)',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 3px',
                    lineHeight: 1
                  }}>
                    {totalUnreadChats > 9 ? '9+' : totalUnreadChats}
                  </span>
                )}
                {!hasDmUnlocked && (
                  <span style={{
                    position: 'absolute', top: -2, right: -2,
                    background: '#f59e0b', color: '#fff',
                    borderRadius: '50%', width: 14, height: 14,
                    fontSize: 9, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1,
                  }}>🔒</span>
                )}
              </button>

              {showChatPanel && (
                <div className="card shell-panel" onClick={e => e.stopPropagation()}
                  style={{ width:340, height:500, marginBottom:0, display:'flex', flexDirection:'column', padding:0 }}>

                  {activeChatConv ? (
                    <>
                      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                        <button onClick={() => { setActiveChatConv(null); setChatMessages([]); }}
                          className="qeduvo-icon-btn" style={{ width:30, height:30 }} aria-label="Back"><ArrowLeft size={18} /></button>
                        <Avatar user={{ name: activeChatConv.other_name, avatar_url: activeChatConv.other_avatar }} size={34} />
                        <div>
                          <div style={{ fontWeight:600, fontSize:14 }}>{activeChatConv.other_name}</div>
                          <div style={{ fontSize:11, color:'var(--accent3)', display:'flex', alignItems:'center', gap:4 }}>
                            <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--accent3)', display:'inline-block' }} /> Online now
                          </div>
                        </div>
                      </div>

                       <div style={{ flex:1, overflowY:'auto', padding:'14px 16px', display:'flex', flexDirection:'column', gap:6 }}>
                        {(() => {
                          if (chatMessages.length === 0) {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 250, color: 'var(--text3)', padding: 24, textAlign: 'center' }}>
                                <MessageCircle size={36} style={{ opacity: 0.3, marginBottom: 8, color: 'var(--accent)' }} />
                                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>No messages yet</div>
                                <div style={{ fontSize: 11, marginTop: 4 }}>Send a message to start the conversation!</div>
                              </div>
                            );
                          }
                          const list = [];
                          let lastDate = '';
                          for (const msg of chatMessages) {
                            const dl = dateLabel(msg.createdAt || msg.created_at);
                            if (dl !== lastDate) {
                              list.push({ type: 'divider', label: dl, key: `div-${msg.id}` });
                              lastDate = dl;
                            }
                            list.push({ type: 'msg', msg, key: msg.id });
                          }
                          return list.map(item => {
                            if (item.type === 'divider') {
                              return (
                                <div key={item.key} style={{
                                  textAlign: 'center', fontSize: 10, color: 'var(--text3)',
                                  margin: '12px 0 6px', fontWeight: 600,
                                  display: 'flex', alignItems: 'center', gap: 8,
                                }}>
                                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                                  {item.label}
                                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                                </div>
                              );
                            }
                            const { msg } = item;
                            const mine = (msg.senderId || msg.sender_id) === user?.id;
                            return (
                              <div key={item.key} style={{ display:'flex', flexDirection: mine ? 'row-reverse' : 'row', gap:8, alignItems:'flex-end', marginBottom: 4 }}>
                                {!mine && (
                                  <Avatar user={{ name: msg.senderName || msg.sender_name, avatar_url: msg.senderAvatar || msg.sender_avatar }} size={28} />
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', maxWidth: '72%' }}>
                                  <div style={{ padding:'9px 13px', borderRadius: mine ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
                                    background: mine ? 'var(--accent)' : 'var(--surface2)', color: mine ? '#fff' : 'var(--text)', fontSize:13, lineHeight:1.5 }}>
                                    {msg.content && <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</div>}
                                    {msg.attachmentUrl && renderAttachmentPreview(msg.attachmentUrl, msg.attachmentType, msg.attachmentName, mine)}
                                  </div>
                                  <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3, paddingLeft: 4, paddingRight: 4 }}>
                                    {timeStr(msg.createdAt || msg.created_at)}
                                    {mine && (
                                      <span style={{ color: msg.isRead ? 'var(--accent)' : 'var(--text3)', fontSize: 10 }}>
                                        {msg.isRead ? '✓✓' : '✓'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                        <div ref={chatEndRef} />
                      </div>

                      <div style={{ padding:'10px 12px', borderTop:'1px solid var(--border)', display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                        <input
                          type="file"
                          ref={chatFileInputRef}
                          onChange={handleChatFileChange}
                          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z"
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          onClick={() => chatFileInputRef.current?.click()}
                          disabled={uploadingChat}
                          title="Attach file"
                          style={{
                            background: 'var(--surface2)',
                            border: '1px solid var(--border)',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            color: 'var(--text2)',
                            flexShrink: 0,
                            opacity: uploadingChat ? 0.4 : 1
                          }}
                        >
                          {uploadingChat ? '⏳' : '📎'}
                        </button>
                        <input type="text" placeholder="Type a message…" value={chatInput}
                          onChange={e => setChatInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
                          style={{ flex:1, padding:'8px 12px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', fontSize:13, outline:'none' }} />
                        <button onClick={sendChatMessage} className="btn btn-primary" style={{ padding:'8px 12px', borderRadius:10 }} aria-label="Send"><Send size={14} /></button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
                        <div style={{ fontWeight:700, fontSize:15, marginBottom:10 }}>Chats</div>
                        <div style={{ background:'var(--surface2)', borderRadius:8, display:'flex', alignItems:'center', padding:'7px 12px', gap:8 }}>
                          <Search size={15} style={{ color:'var(--text3)' }} />
                          <input type="text" placeholder="Search users" value={chatSearch} onChange={e => setChatSearch(e.target.value)}
                            style={{ border:'none', background:'transparent', outline:'none', width:'100%', fontSize:13, color:'var(--text)' }} />
                        </div>
                      </div>
                      <div style={{ flex:1, overflowY:'auto' }}>
                        {filteredConvs.length === 0 ? (
                          <div style={{ padding:28, textAlign:'center', color:'var(--text3)', fontSize:13 }}>No conversations yet</div>
                        ) : filteredConvs.map(conv => {
                          const isActive = activeChatConv?.id === conv.id;
                          return (
                            <div key={conv.id} className="dropdown-item-hover"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '10px 16px',
                                cursor: 'pointer',
                                background: isActive ? 'var(--accent-soft)' : 'transparent',
                                borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                                paddingLeft: isActive ? 13 : 16
                              }}
                              onClick={() => openChat(conv)}>
                              <div style={{ position:'relative', flexShrink:0 }}>
                                <Avatar user={{ name: conv.other_name, avatar_url: conv.other_avatar }} size={40} />
                                <div style={{ position:'absolute', bottom:1, right:1, width:10, height:10, borderRadius:'50%', background:'var(--accent3)', border:'2px solid var(--surface)' }} />
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontWeight:600, fontSize:13 }}>{conv.other_name}</div>
                                <div style={{ fontSize:12, color:'var(--text3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {conv.last_message || 'Start a conversation…'}
                                </div>
                              </div>
                              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                                <div style={{ fontSize:11, color:'var(--text3)' }}>{conv.last_at ? timeAgo(conv.last_at) : ''}</div>
                                {conv.unread_count > 0 && (
                                  <span style={{
                                    background: 'var(--accent)',
                                    color: '#fff',
                                    borderRadius: 10,
                                    minWidth: 16,
                                    height: 16,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 4px',
                                    lineHeight: 1
                                  }}>
                                    {conv.unread_count}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="shell-notif-area" style={{ position:'relative' }}>
              <button className="qeduvo-icon-btn" style={{ position:'relative' }} aria-label="Notifications"
                onClick={() => { const v = !showNotifPanel; closeAll(); setShowNotifPanel(v); if (v) fetchNotifs(); }}>
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span style={{ position:'absolute', top:4, right:4, minWidth:16, height:16, background:'var(--accent2)', borderRadius:8, border:'2px solid var(--surface)', fontSize:9, fontWeight:700, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px', lineHeight:1 }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifPanel && (
                <div className="card shell-panel" onClick={e => e.stopPropagation()}
                  style={{ width:360, maxHeight:520, marginBottom:0, display:'flex', flexDirection:'column', padding:0 }}>

                  <div style={{ padding:'14px 16px 0', flexShrink:0 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                      <div style={{ fontWeight:700, fontSize:15 }}>Notifications</div>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead}
                          style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--accent)', fontWeight:600 }}>
                          Mark all as read
                        </button>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:0, borderBottom:'1px solid var(--border)' }}>
                      {[{key:'all',label:'All'},{key:'like',label:'Likes'},{key:'comment',label:'Comments'},{key:'follow',label:'Followers'}].map(f => (
                        <button key={f.key} onClick={() => setNotifFilter(f.key)}
                          style={{ background:'none', border:'none', cursor:'pointer', padding:'6px 12px', fontSize:12, fontWeight:600,
                            color: notifFilter === f.key ? 'var(--accent)' : 'var(--text3)',
                            borderBottom: notifFilter === f.key ? '2px solid var(--accent)' : '2px solid transparent',
                            marginBottom:-1 }}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ flex:1, overflowY:'auto' }}>
                    {filteredNotifs.length === 0 ? (
                      <div style={{ padding:32, textAlign:'center', color:'var(--text3)', fontSize:13 }}>No notifications</div>
                    ) : filteredNotifs.map(n => {
                      const Icon = NOTIF_ICON[n.type] || Megaphone;
                      return (
                      <div key={n.id} className="dropdown-item-hover"
                        onClick={() => handleNotifClick(n)}
                        style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 16px', cursor:'pointer',
                          background: !n.is_read ? 'var(--accent-soft)' : 'transparent',
                          borderBottom:'1px solid var(--border)' }}>
                        <div style={{ position:'relative', flexShrink:0, width:38, height:38 }}>
                          <Avatar user={{ name: n.actor_name, avatar_url: n.actorAvatar }} size={38} />
                          <div style={{ position:'absolute', bottom:-2, right:-2, width:18, height:18, borderRadius:'50%', background:'var(--surface)', border:'2px solid var(--surface)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--accent)' }}>
                            <Icon size={10} />
                          </div>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, color:'var(--text)', lineHeight:1.4 }}>
                            <strong>{n.actor_name}</strong> {n.message}
                          </div>
                          <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>{timeAgo(n.created_at)} ago</div>
                        </div>
                        {!n.is_read && <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--accent)', flexShrink:0, marginTop:5 }} />}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* User Dropdown */}
            <div className="qeduvo-user-dropdown-area" style={{ position:'relative' }}>
              <div className="qeduvo-avatar" onClick={() => { const v = !showUserDrop; closeAll(); setShowUserDrop(v); }}
                title={user?.display_name} style={{ cursor:'pointer' }}>
                {avatarUrl ? <img src={avatarUrl} alt="Avatar" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }} /> : initial}
              </div>

              {showUserDrop && (
                <div className="card shell-panel" onClick={e => e.stopPropagation()}
                  style={{ width:240, padding:'8px 0', marginBottom:0 }}>
                  <div style={{ padding:'8px 16px 12px', borderBottom:'1px solid var(--border)' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>{user?.email}</div>
                  </div>
                  <div style={{ padding:'8px 0' }}>
                    {[
                      { label:'Profile',    icon:User,     fn: () => { navigate('/@' + (user?.display_name?.split(' ')[0]?.toLowerCase() || 'me')); setShowUserDrop(false); } },
                      { label:'Settings',   icon:Settings, fn: () => { navigate('/settings'); setShowUserDrop(false); } },
                      { label:'Affiliates', icon:DollarSign, fn: () => { navigate('/settings'); setShowUserDrop(false); } },
                    ].map(item => (
                      <div key={item.label} className="dropdown-item-hover"
                        style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 16px', fontSize:13, cursor:'pointer', color:'var(--text)' }}
                        onClick={item.fn}><item.icon size={16} style={{ color:'var(--text3)' }} />{item.label}</div>
                    ))}
                  </div>
                  <div className="divider" style={{ margin:'4px 0' }} />
                  <div style={{ padding:'8px 0' }}>
                    {[
                      { label:'Create a community', icon:Plus,    fn: () => { navigate('/create'); setShowUserDrop(false); } },
                      { label:'Discover communities', icon:Compass, fn: () => { navigate('/'); setShowUserDrop(false); } },
                    ].map(item => (
                      <div key={item.label} className="dropdown-item-hover"
                        style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 16px', fontSize:13, cursor:'pointer', color:'var(--text2)' }}
                        onClick={item.fn || undefined}><item.icon size={16} style={{ color:'var(--text3)' }} />{item.label}</div>
                    ))}
                  </div>
                  <div className="divider" style={{ margin:'4px 0' }} />
                  <div className="dropdown-item-hover" style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 16px', fontSize:13, cursor:'pointer', color:'var(--accent2)', fontWeight:600 }}
                    onClick={handleLogout}><LogOut size={16} />Log out</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

        {/* ── Sub Nav (desktop / tablet) ── */}
        {!isAdmin && (
          <div className="qeduvo-subnav-container">
            <div className="qeduvo-subnav">
              {isSearchActive ? (
                [
                  { key: 'community', label: 'Community', count: matchingPosts.length },
                  { key: 'classroom', label: 'Classroom', count: matchingCourses.length },
                  { key: 'calendar', label: 'Calendar', count: matchingEvents.length },
                  { key: 'members', label: 'Members', count: matchingMembers.length }
                ].map(n => (
                  <div 
                    key={n.key} 
                    className={'qeduvo-nav-item' + (activeSearchTab === n.key ? ' active' : '')} 
                    onClick={() => setActiveSearchTab(n.key)}
                  >
                    {t('nav.' + n.key, n.label)} <span className="search-tab-count" style={{ marginLeft: 6, opacity: 0.7, fontSize: '0.85em' }}>{n.count}</span>
                  </div>
                ))
              ) : (
                ALL_NAV.map(n => (
                  <div key={n.key} className={'qeduvo-nav-item' + (page === n.key ? ' active' : '')} onClick={() => navigate('/' + (slug || 'app') + '/' + n.key)}>
                    {t('nav.' + n.key, n.label)}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Main Content ── */}
        <main className="qeduvo-main">
          <div className="qeduvo-content">
            {isAdmin ? (
              PAGES[page] || <div className="flex-center text-muted">Page not found</div>
            ) : activeCommunity ? (
              isSearchActive ? (
                PAGES[activeSearchTab] || <div className="flex-center text-muted">Page not found</div>
              ) : (
                PAGES[page] || <div className="flex-center text-muted">Page not found</div>
              )
            ) : (
              <div className="flex-center text-center flex-col" style={{ height: '60vh', padding: 40 }}>
                <div style={{ width:84, height:84, borderRadius:24, background:'var(--accent-soft)', color:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
                  <Compass size={42} />
                </div>
                <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Welcome to Qeduvo!</h2>
                <p style={{ color: 'var(--text2)', maxWidth: 460, marginBottom: 28, lineHeight: 1.6 }}>
                  You have not joined or created any communities yet. Find active communities or create your own to get started.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap:'wrap', justifyContent:'center' }}>
                  <button className="btn btn-primary btn-lg" onClick={() => navigate('/')}><Compass size={18} />Discover Communities</button>
                  <button className="btn btn-outline btn-lg" onClick={() => navigate('/create')}><Plus size={18} />Create Community</button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── Mobile navigation drawer ── */}
      {showDrawer && (
        <>
          <div className="mobile-drawer-overlay" onClick={() => setShowDrawer(false)} />
          <nav className="mobile-drawer">
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, padding:'4px 6px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, fontWeight:800, fontSize:16 }}>
                <div className="qeduvo-logo-img" style={{
                  background: activeCommunity?.coverImageUrl
                    ? 'transparent'
                    : 'var(--accent-grad)',
                  color: '#fff',
                  fontSize: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {activeCommunity?.coverImageUrl ? (
                    <img
                      src={`${API_URL}${activeCommunity.coverImageUrl}`}
                      alt=""
                      style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }}
                    />
                  ) : (
                    activeCommunity?.emoji || <GraduationCap size={18} />
                  )}
                </div>
                {activeCommunity ? activeCommunity.name : 'Qeduvo'}
              </div>
              <button className="qeduvo-icon-btn" onClick={() => setShowDrawer(false)} aria-label="Close menu"><X size={20} /></button>
            </div>

            <div style={{ flex:1, overflowY:'auto' }}>
              {isSearchActive ? (
                [
                  { key: 'community', label: 'Community', count: matchingPosts.length },
                  { key: 'classroom', label: 'Classroom', count: matchingCourses.length },
                  { key: 'calendar', label: 'Calendar', count: matchingEvents.length },
                  { key: 'members', label: 'Members', count: matchingMembers.length }
                ].map(n => (
                  <div 
                    key={n.key} 
                    className={'mobile-drawer-item' + (activeSearchTab === n.key ? ' active' : '')} 
                    onClick={() => {
                      setActiveSearchTab(n.key);
                      setShowDrawer(false);
                    }}
                  >
                    {t('nav.' + n.key, n.label)} <span style={{ marginLeft: 'auto', opacity: 0.7, fontSize: '0.85em' }}>({n.count})</span>
                  </div>
                ))
              ) : (
                ALL_NAV.map(n => (
                  <div key={n.key} className={'mobile-drawer-item' + (page === n.key ? ' active' : '')} onClick={() => goToPage(n.key)}>
                    {t('nav.' + n.key, n.label)}
                  </div>
                ))
              )}
            </div>

            <div className="divider" />
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'4px 10px' }}>
              <span style={{ fontSize:13, color:'var(--text3)', fontWeight:600 }}>Appearance & language</span>
              <span style={{ display:'flex', alignItems:'center', gap:6 }}><ThemeSwitcher /><LangSwitcher /></span>
            </div>
            <div className="mobile-drawer-item" onClick={() => { navigate('/settings'); setShowDrawer(false); }}><Settings size={18} />Settings</div>
            <div className="mobile-drawer-item" onClick={() => { navigate('/'); setShowDrawer(false); }}><Compass size={18} />Discover communities</div>
            <div className="mobile-drawer-item" style={{ color:'var(--accent2)' }} onClick={handleLogout}><LogOut size={18} />Log out</div>
          </nav>
        </>
      )}

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {chatTarget && (
        <ChatDrawer
          targetUser={chatTarget}
          communityId={activeCommunity?.id}
          onClose={() => setChatTarget(null)}
        />
      )}
      {previewImage && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            cursor: 'zoom-out',
            pointerEvents: 'all'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setPreviewImage(null);
          }}
        >
          <button 
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: 44,
              height: 44,
              cursor: 'pointer',
              color: '#fff',
              fontSize: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setPreviewImage(null);
            }}
          >
            ✕
          </button>
          <img 
            src={previewImage} 
            alt="Preview" 
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
              cursor: 'default'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
